/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Groups3';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { CIColors, ZodTypes } from 'shared';

import { notifyReportableApiError } from '../../../../components/notifyReportableApiError';
import { QueryLoader } from '../../../../components/QueryLoader';
import { SelectWithDescription } from '../../../../components/SelectWithDescription/SelectWithDescription';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { TeamApiService } from '../../../../services/TeamApiService';
import { UserApiService } from '../../../../services/UserApiService';
import { fetchOrganizationUsers, fetchTeam } from '../../../../utils/queryFns';
import { useOrganizationPermissions } from '../../../../utils/useOrganizationPermissions';
import { useOrganizationRolesI18n } from '../../../../utils/useOrganizationRolesI18n';
import { EditableTypography } from './EditableTypography';
import { GroupPicker } from './GroupPicker';
import { MemberPicker } from './MemberPicker';

const useStyles = makeStyles(theme => ({
  // Team
  teamWrapper: {
    gap: theme.spacing(1),
    width: '300px',
    height: 'fit-content',
    padding: theme.spacing(2.5),
    borderRadius: '12px',
    backgroundColor: '#F6F7F8',
  },

  // Table
  tableWrapper: {
    display: 'grid',
    width: '100%',
    height: 'fit-content',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoRows: 'minmax(100px, 1fr) repeat(minmax(100px, 1fr), 1px)',
    alignItems: 'center',
  },
  tableDivider: {
    gridColumn: '1 / -1',
    height: 1,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    background: theme.palette.divider,
  },
  tableHoverEdit: {
    '& > :last-child': {
      visibility: 'hidden',
    },
    '&:hover > :last-child': {
      visibility: 'visible',
    },
  },

  // Input
  displayName: {
    fontSize: '13px',
  },
  about: {
    fontSize: '11px',
  },
}));

type Props = {
  organizationId: string;
  teamIdentifier: string;
};

type User = { id: string; firstName: string; lastName: string; email: string };
type UserName = Pick<User, 'firstName' | 'lastName'>;
const sortUsers = (a: UserName, b: UserName) =>
  a.firstName.localeCompare(b.firstName) && a.lastName.localeCompare(b.lastName);

export const Team = memo<Props>(props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const permissions = useOrganizationPermissions(props.organizationId);

  const organizationRoles = useOrganizationRolesI18n();

  const [isAddMemberPickerOpen, setIsAddMemberPickerOpen] = useState(false);
  const openAddMemberPicker = useCallback(() => setIsAddMemberPickerOpen(true), [setIsAddMemberPickerOpen]);
  const closeAddMemberPicker = useCallback(() => setIsAddMemberPickerOpen(false), [setIsAddMemberPickerOpen]);

  const [isAddGroupPickerOpen, setIsAddGroupPickerOpen] = useState(false);
  const openAddGroupPicker = useCallback(() => setIsAddGroupPickerOpen(true), [setIsAddGroupPickerOpen]);
  const closeAddGroupPicker = useCallback(() => setIsAddGroupPickerOpen(false), [setIsAddGroupPickerOpen]);

  const organizationUsersQuery = useQuery(
    [fetchOrganizationUsers.key, { organizationId: props.organizationId }],
    fetchOrganizationUsers.fn,
  );
  const organizationUsers = useMemo(
    () =>
      organizationUsersQuery.data?.payload
        .map(u => ({
          id: u.user.id,
          firstName: u.user.firstName,
          lastName: u.user.lastName,
          email: u.user.email,
        }))
        .sort(sortUsers) || [],
    [organizationUsersQuery.data],
  );

  const teamQuery = useQuery(
    [fetchTeam.key, { organizationId: props.organizationId, teamIdentifier: props.teamIdentifier }],
    fetchTeam.fn,
  );
  const team = useMemo(() => {
    const team = teamQuery.data?.payload;
    if (team) {
      team.users = team.users.sort(sortUsers);
    }
    return team;
  }, [teamQuery.data]);

  const teamMembers = useMemo(() => {
    const { users, ssoGroups } = team || { users: [], ssoGroups: [] };

    return [
      ...users.map(user => ({ type: 'user' as const, ...user })),
      ...ssoGroups.map(group => ({ type: 'ssoGroup' as const, ...group })),
    ];
  }, [team]);

  const nonTeamMembers = useMemo(
    () => organizationUsers.filter(user => !team?.users.some(u => u.id === user.id)),
    [organizationUsers, team],
  );

  const addUsers = useCallback(
    async (values: { userIds: string[]; groupNames: string[] }) => {
      try {
        await TeamApiService.addTeamUser({ ...props, ...values }).catch(notifyReportableApiError);
        closeAddMemberPicker();
        closeAddGroupPicker();
      } catch (error) {
        notifyReportableApiError(error).catch(error => {
          console.log('Failed to notify reportable api:', error);
        });
      }
    },
    [closeAddGroupPicker, closeAddMemberPicker, props],
  );

  const [memberToBeRemoved, setMemberToBeRemoved] = useState<(typeof teamMembers)[number] | null>(null);
  const clearUserToBeRemoved = useCallback(() => setMemberToBeRemoved(null), [setMemberToBeRemoved]);
  const handleUserRemoval = useCallback(async () => {
    if (memberToBeRemoved) {
      await TeamApiService.removeTeamUser({
        ...props,
        target:
          memberToBeRemoved.type === 'user'
            ? { userId: memberToBeRemoved.id }
            : { groupName: memberToBeRemoved.groupName },
      }).catch(notifyReportableApiError);
      clearUserToBeRemoved();
    }
  }, [props, memberToBeRemoved, clearUserToBeRemoved]);

  return (
    <QueryLoader queries={[teamQuery, organizationUsersQuery]}>
      <Stack direction="row" spacing={2}>
        <Stack className={classes.teamWrapper}>
          <Typography variant="h4">{t('organizationAdmin:teams.properties.displayName.title')}</Typography>
          <EditableTypography
            className={classes.displayName}
            onSubmit={value =>
              TeamApiService.patchTeam({ ...props, data: { displayName: value!.trim() } }).catch(
                notifyReportableApiError,
              )
            }
            schema={Yup.string()
              .trim()
              .required(t('organizationAdmin:teams.properties.displayName.errors.required'))
              .matches(
                ZodTypes.Team.DisplayNameRegex,
                t('organizationAdmin:teams.properties.displayName.errors.regex'),
              )}
            value={team?.displayName}
            readOnly={!permissions.edit_team}
            data-cy="team-display-name-input"
          />

          <Divider orientation="horizontal" />

          <Typography variant="h4">{t('organizationAdmin:teams.properties.about.title')}</Typography>
          <EditableTypography
            className={classes.about}
            onSubmit={value =>
              TeamApiService.patchTeam({ ...props, data: { about: value?.trim() || null } }).catch(
                notifyReportableApiError,
              )
            }
            schema={Yup.string().nullable()}
            value={team?.about}
            placeholder={t('organizationAdmin:teams.properties.about.empty_placeholder')}
            readOnly={!permissions.edit_team}
            data-cy="team-about-input"
          />

          <Divider orientation="horizontal" />

          <Typography variant="h4">{t('organizationAdmin:teams.properties.identifier.title')}</Typography>
          <Tooltip arrow title={t('organizationAdmin:teams.properties.identifier.immutable')}>
            <Typography
              variant="caption"
              component="span"
              data-cy="team-identifier-span"
              style={{ padding: '6px 0px 7px 0px' }}
            >
              {team?.identifier}
            </Typography>
          </Tooltip>

          <Divider orientation="horizontal" />
          <Typography variant="h4">{t('organizationAdmin:teams.properties.role.title')}</Typography>
          <SelectWithDescription
            typographyVariant="caption"
            disableUnderline
            onSubmit={role =>
              TeamApiService.patchTeam({
                ...props,
                data: { role: role as (typeof organizationRoles)[number]['key'] },
              }).catch(notifyReportableApiError)
            }
            value={team?.role}
            items={organizationRoles}
            disabled={!permissions.edit_team}
            data-cy="team-role-select"
          />
        </Stack>

        <Box className={classes.tableWrapper}>
          <Stack direction="row" gridColumn="1 / -1" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">
              {t('organizationAdmin:teams.table.number_of_members', { count: team?.users.length })}
            </Typography>

            {permissions.edit_team && (
              <Stack direction="row">
                <Button onClick={openAddMemberPicker} data-cy="team-add-members-button">
                  {t('organizationAdmin:teams.add_members')}
                </Button>
                <Divider flexItem orientation="vertical" />
                <Button onClick={openAddGroupPicker} data-cy="team-add-groups-button">
                  {t('organizationAdmin:teams.add_groups')}
                </Button>
              </Stack>
            )}
          </Stack>

          <Divider className={classes.tableDivider} />

          {teamMembers.length === 0 ? (
            <Box style={{ gridColumn: '1 / -1' }}>{t('organizationAdmin:teams.table.no_members')}</Box>
          ) : (
            teamMembers.map((item, i) => (
              <>
                <Stack
                  direction="row"
                  gridColumn="1 / -1"
                  alignItems="center"
                  justifyContent="space-between"
                  className={permissions.edit_team ? classes.tableHoverEdit : undefined}
                  data-cy={`team-row-${item.type === 'user' ? `${item.firstName}-${item.lastName}` : `${item.groupName}`}`}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ListItemAvatar>
                      {item.type === 'user' ? (
                        <Avatar
                          src={UserApiService.getUserByIdThumbnailUrl({ userId: item.id })}
                          alt={`${item.firstName} ${item.lastName}`}
                        />
                      ) : (
                        <Avatar style={{ backgroundColor: CIColors.Tertiary }}>
                          <GroupIcon />
                        </Avatar>
                      )}
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        item.type === 'user' ? (
                          `${item.firstName} ${item.lastName}`
                        ) : (
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2">{item.groupName}</Typography>
                            <AvatarGroup max={5} sx={{ display: 'flex', justifyContent: 'left' }}>
                              {item.users.map(user => (
                                <Tooltip key={user.id} title={`${user.firstName} ${user.lastName} (${user.email})`}>
                                  <Avatar
                                    src={UserApiService.getUserByIdThumbnailUrl({ userId: user.id })}
                                    alt={`${user.firstName} ${user.lastName}`}
                                    data-cy={`teams-${item.groupName}-avatar-${user.firstName}-${user.lastName}`}
                                    sx={{ width: 24, height: 24 }}
                                  />
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                          </Stack>
                        )
                      }
                      secondary={item.type === 'user' ? item.email : null}
                    />
                  </Stack>
                  {permissions.edit_team && (
                    <Box mx={1}>
                      <IconButton
                        onClick={() => setMemberToBeRemoved(item)}
                        size="small"
                        data-cy={`team-remove-${item.type === 'user' ? `${item.firstName}-${item.lastName}` : item.groupName}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Stack>
                {i + 1 !== teamMembers.length ? <Divider className={classes.tableDivider} /> : null}
              </>
            ))
          )}
        </Box>
        <MemberPicker
          users={nonTeamMembers}
          teams={[]}
          open={isAddMemberPickerOpen}
          onCancel={closeAddMemberPicker}
          onSuccess={({ userIds }) => addUsers({ userIds, groupNames: [] })}
        />
        <GroupPicker
          organizationId={props.organizationId}
          groupsThatAreAlreadyMembers={team?.ssoGroups || []}
          open={isAddGroupPickerOpen}
          onCancel={closeAddGroupPicker}
          onSuccess={groupNames => addUsers({ groupNames, userIds: [] })}
        />
        <SimpleConfirmModal
          open={!!memberToBeRemoved}
          message={
            <Typography>
              {t('organizationAdmin:teams.remove_member_part1')}
              <Typography component="span" style={{ fontWeight: 'bold' }}>
                {memberToBeRemoved?.type === 'user'
                  ? `${memberToBeRemoved?.firstName} ${memberToBeRemoved?.lastName} (${memberToBeRemoved?.email})`
                  : `${memberToBeRemoved?.groupName}`}
              </Typography>
              {t('organizationAdmin:teams.remove_member_part2')}
            </Typography>
          }
          onCancel={clearUserToBeRemoved}
          onConfirm={handleUserRemoval}
        />
      </Stack>
    </QueryLoader>
  );
});
